import { isElectron } from '@/renderer/util';
import { ApiResponse } from '@shared/types';
import { supabase } from './supabase';

export interface OutletEbmConfig {
  id: string;
  outlet_id: string;
  deployment_mode: 'LOCAL_VSDC' | 'HOSTED';
  vsdc_base_url: string | null;
  tin: string | null;
  bhf_id: string;
  dvc_srl_no: string | null;
  activation_status: 'NOT_CONFIGURED' | 'PENDING_INIT' | 'ACTIVE' | 'FAILED';
  last_init_result_cd: string | null;
  last_init_at: string | null;
  last_health_check_at: string | null;
  last_health_ok: boolean | null;
  // Machine Registration Code RRA issues to the POS software product itself
  // (format BBBCCNNNNNN, per the CIS-for-VSDC spec) — distinct from the
  // VSDC's own mrcNo. Not yet issued to smartPOS Cloud; nullable until it is.
  pos_mrc_code: string | null;
  created_at: string;
  updated_at: string;
}

// Explicit column list — hosted_api_key_ciphertext must never reach the
// renderer, even accidentally via a future `select('*')` edit.
const EBM_CONFIG_COLUMNS =
  'id, outlet_id, deployment_mode, vsdc_base_url, tin, bhf_id, dvc_srl_no, ' +
  'activation_status, last_init_result_cd, last_init_at, last_health_check_at, ' +
  'last_health_ok, pos_mrc_code, created_at, updated_at';

export async function fetchEbmConfig(
  outletId: string,
): Promise<OutletEbmConfig | null> {
  const { data, error } = await supabase
    .from('outlet_ebm_configs')
    .select(EBM_CONFIG_COLUMNS)
    .eq('outlet_id', outletId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return data as unknown as OutletEbmConfig | null;
}

export interface UpsertEbmConfigRequest {
  outlet_id: string;
  deployment_mode: 'LOCAL_VSDC' | 'HOSTED';
  vsdc_base_url?: string | null;
  tin?: string | null;
  bhf_id?: string;
  dvc_srl_no?: string | null;
  pos_mrc_code?: string | null;
}

export async function upsertEbmConfig(
  request: UpsertEbmConfigRequest,
): Promise<OutletEbmConfig> {
  const { data, error } = await supabase
    .from('outlet_ebm_configs')
    .upsert(
      { ...request, updated_at: new Date().toISOString() },
      { onConflict: 'outlet_id' },
    )
    .select(EBM_CONFIG_COLUMNS)
    .single();
  if (error) throw new Error(error.message);
  return data as unknown as OutletEbmConfig;
}

function requireElectron(): void {
  if (!isElectron()) {
    throw new Error('EBM device setup is only available in the desktop app.');
  }
}

export interface InitDeviceIpcResult {
  activationStatus: 'ACTIVE' | 'FAILED';
  resultCd: string;
  resultMsg: string;
  hint?: string;
}

export async function initEbmDevice(
  outletId: string,
  dvcSrlNo: string,
): Promise<ApiResponse<InitDeviceIpcResult>> {
  requireElectron();
  const result = await window.electron.ipcRenderer.invoke('ebm:initDevice', {
    outletId,
    dvcSrlNo,
  });
  return result as ApiResponse<InitDeviceIpcResult>;
}

export interface GetHealthIpcResult {
  healthy: boolean;
  checkedAt: string;
  detail?: string;
}

export async function getEbmHealth(
  outletId: string,
): Promise<ApiResponse<GetHealthIpcResult>> {
  requireElectron();
  const result = await window.electron.ipcRenderer.invoke('ebm:getHealth', {
    outletId,
  });
  return result as ApiResponse<GetHealthIpcResult>;
}

export async function setEbmHostedApiKey(
  outletId: string,
  apiKey: string,
): Promise<ApiResponse<void>> {
  requireElectron();
  const result = await window.electron.ipcRenderer.invoke(
    'ebm:setHostedApiKey',
    { outletId, apiKey },
  );
  return result as ApiResponse<void>;
}

export async function clearEbmHostedApiKey(
  outletId: string,
): Promise<ApiResponse<void>> {
  requireElectron();
  const result = await window.electron.ipcRenderer.invoke(
    'ebm:clearHostedApiKey',
    { outletId },
  );
  return result as ApiResponse<void>;
}

export async function hasEbmHostedApiKey(
  outletId: string,
): Promise<ApiResponse<{ hasKey: boolean }>> {
  requireElectron();
  const result = await window.electron.ipcRenderer.invoke(
    'ebm:hasHostedApiKey',
    { outletId },
  );
  return result as ApiResponse<{ hasKey: boolean }>;
}
